import * as stylex from '@stylexjs/stylex'
import type { ComponentPropsWithoutRef } from 'react'

import { breakpoints, colors, sizes, spacing } from '../tokens/tokens.stylex'

const styles = stylex.create({
  surface: { color: colors.foreground },
  page: { backgroundColor: colors.background },
  surfaceTone: { backgroundColor: colors.surface },
  alternate: { backgroundColor: colors.surfaceAlt },
  marketing: { backgroundColor: colors.marketingBackground, color: colors.marketingForeground },
  marketingBorder: { borderBlockEndColor: colors.marketingBorder },
  section: { boxSizing: 'border-box', width: '100%' },
  sectionPadding: { paddingBlock: { default: spacing.space48, [breakpoints.wide]: spacing.space64 } },
  borderEnd: { borderBlockEndColor: colors.border, borderBlockEndStyle: 'solid', borderBlockEndWidth: 1 },
  container: { boxSizing: 'border-box', marginInline: 'auto', width: '100%' },
  content: { maxWidth: sizes.content },
  gutter: {
    paddingInline: {
      default: spacing.space32,
      [breakpoints.medium]: spacing.space48,
      [breakpoints.wide]: spacing.space64,
    },
  },
  stack: { display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' },
  automaticWidth: { width: 'auto' },
  inline: { display: 'flex', flexWrap: 'wrap', minWidth: 0 },
  grid: { display: 'grid', minWidth: 0 },
  columns1: { gridTemplateColumns: 'minmax(0, 1fr)' },
  columns2: { gridTemplateColumns: { default: 'minmax(0, 1fr)', [breakpoints.medium]: 'repeat(2, minmax(0, 1fr))' } },
  columns3: { gridTemplateColumns: { default: 'minmax(0, 1fr)', [breakpoints.medium]: 'repeat(3, minmax(0, 1fr))' } },
  columns4: {
    gridTemplateColumns: {
      default: 'minmax(0, 1fr)',
      [breakpoints.medium]: 'repeat(2, minmax(0, 1fr))',
      [breakpoints.wide]: 'repeat(4, minmax(0, 1fr))',
    },
  },
  alignStart: { alignItems: 'flex-start' },
  alignCenter: { alignItems: 'center' },
  alignStretch: { alignItems: 'stretch' },
  justifyStart: { justifyContent: 'flex-start' },
  justifyCenter: { justifyContent: 'center' },
  justifyEnd: { justifyContent: 'flex-end' },
  justifyBetween: { justifyContent: 'space-between' },
  nowrap: { flexWrap: 'nowrap' },
  separator: {
    borderWidth: 0,
    borderBlockStartWidth: 1,
    borderBlockStartStyle: 'solid',
    borderBlockStartColor: colors.border,
    margin: 0,
    width: '100%',
  },
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
    borderWidth: 0,
  },
})

const gaps = stylex.create({
  0: { gap: spacing.space0 },
  2: { gap: spacing.space2 },
  4: { gap: spacing.space4 },
  6: { gap: spacing.space6 },
  8: { gap: spacing.space8 },
  10: { gap: spacing.space10 },
  12: { gap: spacing.space12 },
  14: { gap: spacing.space14 },
  16: { gap: spacing.space16 },
  20: { gap: spacing.space20 },
  24: { gap: spacing.space24 },
  28: { gap: spacing.space28 },
  32: { gap: spacing.space32 },
  36: { gap: spacing.space36 },
  40: { gap: spacing.space40 },
  44: { gap: spacing.space44 },
  48: { gap: spacing.space48 },
  56: { gap: spacing.space56 },
  64: { gap: spacing.space64 },
})

export type Space = keyof typeof gaps
type ElementProps = Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'style' | 'color'>
type Tone = 'page' | 'surface' | 'alternate' | 'marketing'
type Alignment = 'start' | 'center' | 'stretch'

const tones = {
  page: styles.page,
  surface: styles.surfaceTone,
  alternate: styles.alternate,
  marketing: styles.marketing,
}

const alignments = { start: styles.alignStart, center: styles.alignCenter, stretch: styles.alignStretch }

const justifications = {
  start: styles.justifyStart,
  center: styles.justifyCenter,
  end: styles.justifyEnd,
  between: styles.justifyBetween,
}

const columnsStyles = { 1: styles.columns1, 2: styles.columns2, 3: styles.columns3, 4: styles.columns4 }

type SurfaceOptions = { tone?: Tone }

export type SurfaceProps = ElementProps & SurfaceOptions

type ThemeAttributes = { 'data-softmax-theme'?: 'light' }

/**
 * Use Surface to set a region’s background and foreground without establishing page geometry.
 * Choose PageSurface for a product page and Section for a spaced page band.
 * Applications supply the content and choose the surrounding layout.
 *
 * Status: supported.
 */
export function Surface({ tone = 'page', ...props }: SurfaceProps) {
  const themeProps: ThemeAttributes = {}

  if (tone === 'marketing') themeProps['data-softmax-theme'] = 'light'

  return <div {...props} {...themeProps} {...stylex.props(styles.surface, tones[tone])} />
}

type SectionOptions = { tone?: Tone; padding?: 'none' | 'section'; border?: 'none' | 'end' }

export type SectionProps = ElementProps & SectionOptions

/**
 * Use Section for a semantic page band with shared spacing and an optional ending border.
 * Choose Surface when only the region’s colors should change.
 * Applications supply the content and its document hierarchy.
 *
 * Status: supported.
 */
export function Section({ tone = 'page', padding = 'section', border = 'none', ...props }: SectionProps) {
  const themeProps: ThemeAttributes = {}

  if (tone === 'marketing') themeProps['data-softmax-theme'] = 'light'

  return (
    <section
      {...props}
      {...themeProps}
      {...stylex.props(
        styles.surface,
        tones[tone],
        styles.section,
        padding === 'section' && styles.sectionPadding,
        border === 'end' && styles.borderEnd,
        border === 'end' && tone === 'marketing' && styles.marketingBorder
      )}
    />
  )
}

type ContainerOptions = { width?: 'content' | 'full'; gutter?: 'none' | 'page' }

export type ContainerProps = ElementProps & ContainerOptions

/**
 * Use Container to constrain content width and align responsive page gutters.
 * Choose Stack or Grid to arrange the content inside it.
 * Applications decide whether the region shares page gutters or spans its parent.
 *
 * Status: supported.
 */
export function Container({ width = 'content', gutter = 'page', ...props }: ContainerProps) {
  return (
    <div
      {...props}
      {...stylex.props(styles.container, width === 'content' && styles.content, gutter === 'page' && styles.gutter)}
    />
  )
}

type StackOptions = { gap?: Space; align?: Alignment; width?: 'auto' | 'full' }

export type StackProps = ElementProps & StackOptions

/**
 * Use Stack for a vertical group of related content with shared spacing.
 * Choose Inline for a row or Grid for responsive columns.
 * Applications supply content in reading order and choose whether the group fills its parent.
 *
 * Status: supported.
 */
export function Stack({ gap = 16, align = 'stretch', width = 'full', ...props }: StackProps) {
  return (
    <div
      {...props}
      {...stylex.props(styles.stack, width === 'auto' && styles.automaticWidth, gaps[gap], alignments[align])}
    />
  )
}

type InlineOptions = {
  gap?: Space
  align?: Alignment
  justify?: 'start' | 'center' | 'end' | 'between'
  wrap?: boolean
}

export type InlineProps = ElementProps & InlineOptions

/**
 * Use Inline for related controls or labels that may wrap onto another row.
 * Choose Stack for a vertical sequence or Grid for aligned columns.
 * Applications supply content in reading order and choose its alignment and wrapping.
 *
 * Status: supported.
 */
export function Inline({ gap = 8, align = 'center', justify = 'start', wrap = true, ...props }: InlineProps) {
  return (
    <div
      {...props}
      {...stylex.props(styles.inline, gaps[gap], alignments[align], justifications[justify], !wrap && styles.nowrap)}
    />
  )
}

type GridOptions = { columns?: 1 | 2 | 3 | 4; gap?: Space }

export type GridProps = ElementProps & GridOptions

/**
 * Use Grid for content that needs a shared column layout and collapses at narrow widths.
 * Choose Inline for wrapping controls and Stack for a vertical sequence.
 * Applications supply content in reading order and choose the column count.
 *
 * Status: supported.
 */
export function Grid({ columns = 1, gap = 24, ...props }: GridProps) {
  return <div {...props} {...stylex.props(styles.grid, columnsStyles[columns], gaps[gap])} />
}

export type SeparatorProps = Omit<ComponentPropsWithoutRef<'hr'>, 'className' | 'style' | 'color'>

/**
 * Use Separator to mark a thematic break between related regions.
 * Choose spacing alone when the separation adds no meaning.
 * Applications decide whether the separator is semantic or decorative through native attributes.
 *
 * Status: supported.
 */
export function Separator(props: SeparatorProps) {
  return <hr {...props} {...stylex.props(styles.separator)} />
}

export type VisuallyHiddenProps = Omit<ComponentPropsWithoutRef<'span'>, 'className' | 'style' | 'color'>

/**
 * Use VisuallyHidden for text that names or describes nearby content for assistive technology.
 * Prefer visible text when every reader needs the information.
 * Applications supply the wording and associate it with the intended control.
 *
 * Status: supported.
 */
export function VisuallyHidden(props: VisuallyHiddenProps) {
  return <span {...props} {...stylex.props(styles.visuallyHidden)} />
}
